import { ChangeDetectionStrategy, Component, OnInit, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { DividerModule } from 'primeng/divider';
import { SkeletonModule } from 'primeng/skeleton';
import { finalize } from 'rxjs';
import { Measurement } from '@cosmos/shared';
import { ErrorService, PanelService } from '@cosmos/angular/utils';
import { IngestedCsvService, RawCsvRow } from '@cosmos/angular/api';

@Component({
  selector: 'cosmos-raw-data-viewer',
  imports: [
    DatePipe, TranslatePipe,
    ButtonModule, TableModule, TagModule, TooltipModule, MessageModule, DividerModule, SkeletonModule,
  ],
  templateUrl: './raw-data-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RawDataViewerComponent implements OnInit {
  private readonly ingestedCsvService = inject(IngestedCsvService);
  private readonly errorService = inject(ErrorService);
  private readonly panelService = inject(PanelService);

  readonly measurement = input.required<Measurement>();

  readonly rows = signal<RawCsvRow[]>([]);
  readonly loading = signal(false);
  readonly selected = signal<RawCsvRow[]>([]);
  readonly downloading = signal(false);
  private readonly downloadingId = signal<string | null>(null);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.ingestedCsvService
      .getForMeasurement(this.measurement()._id!)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (res) => this.rows.set(res.body ?? []),
        error: (e) => this.errorService.handleError(e),
      });
  }

  tagSeverity(status: RawCsvRow['status']): 'success' | 'warn' | 'secondary' {
    switch (status) {
      case 'processed': return 'success';
      case 'orphan': return 'warn';
      case 'removed': return 'secondary';
    }
  }

  isDownloadingRow(row: RawCsvRow): boolean {
    return this.downloadingId() === row._id;
  }

  downloadOne(row: RawCsvRow): void {
    this.downloadingId.set(row._id);
    this.ingestedCsvService
      .download(this.measurement()._id!, row._id)
      .pipe(finalize(() => this.downloadingId.set(null)))
      .subscribe({
        next: (res) => this.saveBlob(res.body!, row.filename),
        error: (e) => this.errorService.handleError(e),
      });
  }

  downloadSelected(): void {
    const ids = this.selected().map((r) => r._id);
    if (ids.length) this.bulk(ids);
  }

  downloadAll(): void {
    this.bulk(this.rows().map((r) => r._id));
  }

  private bulk(ids: string[]): void {
    this.downloading.set(true);
    this.ingestedCsvService
      .downloadBulk(this.measurement()._id!, ids)
      .pipe(finalize(() => this.downloading.set(false)))
      .subscribe({
        next: (res) => this.saveBlob(res.body!, `raw-csv-${this.measurement()._id}.zip`),
        error: (e) => this.errorService.handleError(e),
      });
  }

  private saveBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  close(): void {
    this.panelService.close();
  }
}
